import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';

interface Client {
  id: string;
  name: string;
}

export const useUserClients = () => {
  const { profile } = useAuth();

  const { data: clients = [], isLoading } = useQuery({
    queryKey: ['user-clients', profile?.id],
    queryFn: async () => {
      if (!profile?.id) return [];

      // Buscar dados do usuário para obter client_id e client_ids
      const { data: userData, error: userError } = await supabase
        .from('users')
        .select('client_id, client_ids, role')
        .eq('id', profile.id)
        .single();

      if (userError) {
        console.error('Erro ao buscar dados do usuário:', userError);
        return [];
      }

      // Se for admin, buscar todos os clientes
      if (userData?.role === 'admin') {
        const { data: allClients, error: clientsError } = await supabase
          .from('clients')
          .select('id, name')
          .eq('status', 'active')
          .order('name');

        if (clientsError) {
          console.error('Erro ao buscar clientes:', clientsError);
          return [];
        }

        return allClients as Client[];
      }

      // Para não-admins, coletar todos os client_ids aos quais tem acesso
      const clientIds: string[] = [];

      // Adicionar client_id principal
      if (userData?.client_id) {
        clientIds.push(userData.client_id);
      }

      // Adicionar client_ids do array (se existir)
      if (userData?.client_ids) {
        try {
          // client_ids é do tipo Json (jsonb), precisa ser parseado
          const clientIdsArray = typeof userData.client_ids === 'string' 
            ? JSON.parse(userData.client_ids) 
            : userData.client_ids;
          
          if (Array.isArray(clientIdsArray)) {
            clientIds.push(...clientIdsArray.map(id => String(id)));
          }
        } catch (error) {
          console.error('Erro ao parsear client_ids:', error);
        }
      }

      // Remover duplicatas
      const uniqueClientIds = [...new Set(clientIds)];

      if (uniqueClientIds.length === 0) {
        return [];
      }

      // Buscar informações dos clientes
      const { data: userClients, error: clientsError } = await supabase
        .from('clients')
        .select('id, name')
        .in('id', uniqueClientIds)
        .eq('status', 'active')
        .order('name');

      if (clientsError) {
        console.error('Erro ao buscar clientes do usuário:', clientsError);
        return [];
      }

      return userClients as Client[];
    },
    enabled: !!profile?.id,
  });

  return {
    clients,
    isLoading,
    hasMultipleClients: clients.length > 1,
  };
};
